import React, { useState, useMemo, useEffect } from 'react';
import { PageContainer } from '../../Styles/AppStyles';
import { Header } from '../../Components/Header/Header';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { SearchableHeader } from '../../Components/Search/SearchableHeader';
import { DateRangePicker } from '../../Components/Date/Datepicker';
import { useColumnSearches } from '../../Hooks/useColumnSearch';
import useGetCustomData from '../../Hooks/useGetCustomData';

const searchFields = [
  'product_code',
  'product_name',
  'audit_action',
  'order_number',
  'location_name',
];

interface NonAbatixAuditRow {
  audit_id: number;
  created_date: string;
  product_code: string;
  product_name: string;
  audit_action: string;
  order_number: string;
  old_value: string | null;
  new_value: string | null;
  CustomerName: string | null;
  location_name: string;
}

const NonAbatixAuditTrailPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sortField, setSortField] = useState<string>('audit_id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [displayedRows, setDisplayedRows] = useState<NonAbatixAuditRow[]>([]);

  const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

  const handleStartDateChange = (d: Date | null) => { setStartDate(d); setPage(0); };
  const handleEndDateChange = (d: Date | null) => { setEndDate(d); setPage(0); };

  const handleColumnSearch = (field: string, value: string) => {
    setSearch(field, value);
    setPage(0);
  };

  const handleClearColumnSearch = (field: string) => {
    clearSearch(field);
    setPage(0);
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortOrder(order ?? 'desc');
    setPage(0);
  };

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();

    if (startDate) params.set('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.set('endDate', endDate.toISOString().split('T')[0]);

    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });

    if (sortField) {
      params.set('sort_field', sortField);
      params.set('sort_order', sortOrder);
    }

    return `/api/non-abatix/audit-trail?${params.toString()}`;
  }, [startDate, endDate, columnSearches, sortField, sortOrder]);

  const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
  const [auditData, loading] = useGetCustomData(paginatedUrl);

  const totalRows = auditData?.totalCount ?? 0;

  useEffect(() => {
    if (auditData?.data) {
      setDisplayedRows(auditData.data);
    }
  }, [auditData]);

  const columns: ColumnDef<NonAbatixAuditRow>[] = [
    {
      field: 'CustomerName',
      headerName: 'Account',
      width: 180,
      sortable: false,
      renderCell: (params: any) => params.value ?? '—',
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 220,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="location_name"
          headerName="Location"
          searchValue={columnSearches.location_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'product_name',
      headerName: 'Description',
      width: 200,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="product_name"
          headerName="Description"
          searchValue={columnSearches.product_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'product_code',
      headerName: 'Part No.',
      width: 170,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="product_code"
          headerName="Part No."
          searchValue={columnSearches.product_code}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'old_value',
      headerName: 'Previous Qty',
      width: 130,
      sortable: true,
      renderCell: (params: any) => params.value ?? '—',
    },
    {
      field: 'new_value',
      headerName: 'Updated Qty',
      width: 130,
      sortable: true,
      renderCell: (params: any) => params.value ?? '—',
    },
    {
      field: 'created_date',
      headerName: 'Created At (UTC)',
      width: 200,
      sortable: true,
    },
    {
      field: 'audit_action',
      headerName: 'Action',
      width: 160,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="audit_action"
          headerName="Action"
          searchValue={columnSearches.audit_action}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'order_number',
      headerName: 'P21 Order No.',
      width: 180,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="order_number"
          headerName="P21 Order No."
          searchValue={columnSearches.order_number}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
      renderCell: (params: any) => params.value || '—',
    },
  ];

  return (
    <PageContainer>
      <Header title="Non-ABATIX Inventory Audit Trail" count={totalRows} hideCount={false}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleStartDateChange}
          setEndDate={handleEndDateChange}
        />
      </Header>

      <DataTable
        data={displayedRows}
        columns={columns}
        title="Non-ABATIX Inventory Audit Trail"
        pageKey="non-abatix-audit-trail"
        loading={loading}
        serverSidePagination
        serverSideSort
        onSortModelChange={handleSortChange}
        page={page}
        idField="audit_id"
        rowCount={totalRows}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
        hideColumnFilters
        hideSearchBar
      />
    </PageContainer>
  );
};

export default NonAbatixAuditTrailPage;
